import axios from 'axios';
import { API_CONFIG } from '../config/env';

class ApiKeysService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // GET returns masked API key (XXXX*****) - response format: {"endpoint":"EYt3*****"}
  async getApiKey(): Promise<string> {
    try {
      const response = await axios.get<{ endpoint: string } | string>(`${API_CONFIG.BASE_URL}/api-keys`, {
        headers: this.getAuthHeaders(),
      });
      
      // Handle response format: {"endpoint":"EYt3*****"}
      if (typeof response.data === 'string') {
        return response.data;
      }
      if (response.data && typeof response.data === 'object') {
        // Try 'endpoint' property first (most common format)
        if ('endpoint' in response.data && typeof response.data.endpoint === 'string') {
          return response.data.endpoint;
        }
        // Try 'token' property (some APIs might use this)
        if ('token' in response.data && typeof response.data.token === 'string') {
          return response.data.token;
        }
        // Try 'key' property
        if ('key' in response.data && typeof response.data.key === 'string') {
          return response.data.key;
        }
        // Try 'apiKey' property
        if ('apiKey' in response.data && typeof response.data.apiKey === 'string') {
          return response.data.apiKey;
        }
        // Try accessing the first string property if it exists
        const firstStringValue = Object.values(response.data).find(
          (value) => typeof value === 'string' && value.trim().length > 0
        );
        if (firstStringValue) {
          return firstStringValue as string;
        }
      }
      // Fallback for other formats - log the actual response for debugging
      console.warn('Unexpected response format - could not extract API key:', response.data);
      console.warn('Response keys:', response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A');
      // If we get here, the response format is unexpected, but we should still throw
      // to let the caller know something went wrong
      throw new Error('Unexpected API key response format');
    } catch (error) {
      console.error('Error fetching API key:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
        // Re-throw to let the caller handle 404 vs other errors
      }
      throw error;
    }
  }

  // POST generates new API key (replaces existing or creates new) - response format: {"token":"PXAmjId53GZhWr8yzKkeo1RYwS90Mx6n"}
  async generateApiKey(): Promise<string> {
    try {
      const response = await axios.post<{ token: string } | string>(`${API_CONFIG.BASE_URL}/api-keys`, {}, {
        headers: this.getAuthHeaders(),
      });
      
      // Handle response format: {"token":"PXAmjId53GZhWr8yzKkeo1RYwS90Mx6n"} - this is the full uncensored key
      if (typeof response.data === 'string') {
        return response.data;
      }
      if (response.data && typeof response.data === 'object' && 'token' in response.data) {
        return response.data.token;
      }
      // Fallback for other formats
      console.warn('Unexpected response format:', response.data);
      return '';
    } catch (error) {
      console.error('Error generating API key:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  // DELETE removes existing API key
  async deleteApiKey(): Promise<void> {
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/api-keys`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting API key:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }
}

export const apiKeysService = new ApiKeysService();

